/**
 * Email Worker Service — Transparent worker offload with main-thread fallback
 * 
 * Wraps email IMAP polling and MIME parsing behind a unified async API.
 * If a Web Worker is available, network I/O and regex processing run off the main thread.
 * If the worker fails to load, falls back to inline fetch/parse — zero behavior change.
 * 
 * Primary consumer: EmailListPanel (chat-list-panel.tsx) and EmailViewPanel
 * 
 * Usage:
 *   import { emailWorkerService } from "@/services/email-worker-service";
 *   await emailWorkerService.init();
 *   const result = await emailWorkerService.fetchEmails(gatewayUrl, accounts, 50);
 *   const parsed = await emailWorkerService.parseMime(rawBody);
 */

// ============================================================
// Types (shared with worker)
// ============================================================

export interface EmailAccountForWorker {
  id: string;
  email: string;
  imapHost: string;
  imapPort: number;
  authType: string;
  username: string;
  password?: string;
  accessToken?: string;
  color: string;
  enabled: boolean;
}

export interface FetchedEmail {
  id: string;
  uid: number;
  accountId: string;
  accountColor: string;
  from: string;
  subject: string;
  date: string; // ISO string
  seen: boolean;
  hasAttachment?: boolean;
}

export interface EmailFetchResult {
  emails: FetchedEmail[];
  errors: Record<string, string>;
  counts: Record<string, number>;
}

export interface MimeParseResult {
  isRawMime: boolean;
  text: string;
  html: string | null;
}

// ============================================================
// Internal types
// ============================================================

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
};

// ============================================================
// Service Implementation
// ============================================================

class EmailWorkerService {
  private worker: Worker | null = null;
  private workerAvailable = false;
  private pendingRequests = new Map<number, PendingRequest>();
  private nextRequestId = 0;
  private initPromise: Promise<void> | null = null;

  // ============================================================
  // Initialization
  // ============================================================

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    try {
      await this.spawnWorker();
    } catch (e) {
      console.warn('[EmailWorkerService] Worker spawn failed, using main-thread fallback', e);
    }

    console.log(`[EmailWorkerService] Initialized — worker: ${this.workerAvailable ? 'YES' : 'NO (main-thread fallback)'}`);
  }

  private spawnWorker(): Promise<void> {
    if (typeof Worker === 'undefined') {
      return Promise.reject(new Error('Web Workers not supported'));
    }

    return new Promise<void>((resolve, reject) => {
      // P0-I Phase 1S: 30 s timeout + 5 s progress logging (was 5 s
      // hard timeout).  See merkle-tree-worker-service.ts for the full
      // rationale.  Short version: 5 s was too aggressive on Pi4-class
      // Kali Firefox, causing main-thread fallback and scheduler
      // starvation.
      const INIT_TIMEOUT_MS = 30000;
      const start = performance.now();
      const progressInterval = setInterval(() => {
        const elapsed = Math.round((performance.now() - start) / 1000);
        console.warn(
          `[EmailWorkerService] Worker init still pending after ${elapsed}s (will timeout at 30s) — main thread likely saturated`,
        );
      }, 5000);
      const timeout = setTimeout(() => {
        clearInterval(progressInterval);
        reject(new Error(`Worker init timed out after ${INIT_TIMEOUT_MS / 1000}s`));
      }, INIT_TIMEOUT_MS);

      try {
        this.worker = new Worker('/email-worker.js', { type: 'module' });

        this.worker.onmessage = (e: MessageEvent) => {
          this.handleMessage(e);
        };

        this.worker.onerror = (e: ErrorEvent) => {
          console.warn('[EmailWorkerService] Worker error:', e.message);
          clearInterval(progressInterval);
          clearTimeout(timeout);
          this.teardownWorker();
          reject(new Error(e.message));
        };

        const initId = this.nextRequestId++;
        this.pendingRequests.set(initId, {
          resolve: () => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.workerAvailable = true;
            const elapsed = Math.round((performance.now() - start) / 1000);
            if (elapsed >= 5) {
              console.log(
                `[EmailWorkerService] Worker init completed after ${elapsed}s (slow but successful)`,
              );
            }
            resolve();
          },
          reject: (err) => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.teardownWorker();
            reject(err);
          }
        });

        this.worker.postMessage({ task: 'init', id: initId });
      } catch (e) {
        clearInterval(progressInterval);
        clearTimeout(timeout);
        reject(e);
      }
    });
  }

  private teardownWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerAvailable = false;
    for (const [, pending] of this.pendingRequests) {
      pending.reject(new Error('Worker terminated'));
    }
    this.pendingRequests.clear();
  }

  // ============================================================
  // Worker Communication
  // ============================================================

  private handleMessage(e: MessageEvent): void {
    const { id, error, ...result } = e.data;
    const pending = this.pendingRequests.get(id);
    if (!pending) return;
    this.pendingRequests.delete(id);

    if (error) {
      pending.reject(new Error(error));
    } else {
      pending.resolve(result);
    }
  }

  private sendToWorker(task: string, data: Record<string, any>): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.worker || !this.workerAvailable) {
        reject(new Error('Worker not available'));
        return;
      }

      const id = this.nextRequestId++;
      this.pendingRequests.set(id, { resolve, reject });
      this.worker.postMessage({ task, id, ...data });
    });
  }

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Fetch emails from all enabled accounts via IMAP gateway.
   * Routes to worker if available, otherwise fetches on main thread.
   */
  async fetchEmails(
    gatewayUrl: string,
    accounts: EmailAccountForWorker[],
    messagesPerAccount: number
  ): Promise<EmailFetchResult> {
    if (this.workerAvailable) {
      try {
        const result = await this.sendToWorker('fetch-emails', {
          gatewayUrl,
          accounts,
          messagesPerAccount,
        });
        return result as EmailFetchResult;
      } catch (e) {
        console.warn('[EmailWorkerService] Worker fetch failed, falling back to main thread', e);
      }
    }

    // Main-thread fallback — inline fetch
    return this.fetchEmailsMainThread(gatewayUrl, accounts, messagesPerAccount);
  }

  /**
   * Parse raw MIME body content.
   * Routes to worker if available, otherwise parses on main thread.
   */
  async parseMime(body: string): Promise<MimeParseResult> {
    if (this.workerAvailable) {
      try {
        return await this.sendToWorker('parse-mime', { body }) as MimeParseResult;
      } catch (e) {
        console.warn('[EmailWorkerService] Worker parse failed, falling back to main thread', e);
      }
    }

    // Main-thread fallback — inline parse (same logic as email-view-panel.tsx)
    return this.parseMimeMainThread(body);
  }

  // ============================================================
  // Main-thread fallbacks
  // ============================================================

  private async fetchEmailsMainThread(
    gatewayUrl: string,
    accounts: EmailAccountForWorker[],
    messagesPerAccount: number
  ): Promise<EmailFetchResult> {
    const emails: FetchedEmail[] = [];
    const errors: Record<string, string> = {};
    const counts: Record<string, number> = {};

    for (const account of accounts) {
      if (!account.enabled) continue;

      try {
        const credentials: { user: string; pass?: string; accessToken?: string } = {
          user: account.username,
        };
        if (account.authType === "oauth2" && account.accessToken) {
          credentials.accessToken = account.accessToken;
        } else {
          credentials.pass = account.password;
        }

        const response = await fetch(`${gatewayUrl}/api/email/imap/poll`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            host: account.imapHost,
            port: account.imapPort,
            secure: true,
            credentials,
            folder: "INBOX",
            limit: messagesPerAccount,
          }),
        });

        const data = await response.json().catch(() => ({ success: false }));

        if (response.ok && data.success && data.messages) {
          const messages: FetchedEmail[] = data.messages.map((msg: any) => ({
            id: `${account.id}-${msg.uid}`,
            uid: msg.uid,
            accountId: account.id,
            accountColor: account.color,
            from: msg.from,
            subject: msg.subject,
            date: new Date(msg.date).toISOString(),
            seen: msg.seen,
            hasAttachment: msg.hasAttachment,
          }));
          emails.push(...messages);
          counts[account.id] = messages.length;
        } else if (data.error) {
          errors[account.id] = data.error;
          counts[account.id] = 0;
        } else {
          errors[account.id] = 'No messages returned';
          counts[account.id] = 0;
        }
      } catch (err) {
        errors[account.id] = err instanceof Error ? err.message : 'Connection failed';
        counts[account.id] = 0;
      }
    }

    return { emails, errors, counts };
  }

  private parseMimeMainThread(body: string): MimeParseResult {
    if (!body) return { isRawMime: false, text: body, html: null };

    const trimmed = body.trimStart();
    const isRawMime = trimmed.startsWith("--") &&
      (trimmed.includes("Content-Type:") || trimmed.includes("content-type:"));

    if (!isRawMime) return { isRawMime: false, text: body, html: null };

    // Inline MIME extraction (mirrors email-view-panel.tsx extractReadableBody)
    const boundaryMatch = body.match(/--([^\r\n\s]+)/);
    if (!boundaryMatch) return { isRawMime: true, text: body, html: null };

    const boundary = boundaryMatch[1];
    const parts = body.split(new RegExp(`--${boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));

    let plainText = "";
    let htmlText: string | null = null;

    for (const part of parts) {
      if (!part.trim() || part.trim() === "--") continue;

      const headerEnd = part.indexOf("\r\n\r\n") !== -1
        ? part.indexOf("\r\n\r\n")
        : part.indexOf("\n\n");
      if (headerEnd === -1) continue;

      const headers = part.slice(0, headerEnd).toLowerCase();
      const partBody = part.slice(headerEnd).replace(/^[\r\n]+/, "");

      const isPlain = headers.includes("text/plain");
      const isHtml = headers.includes("text/html");
      if (!isPlain && !isHtml) continue;

      let decoded = partBody;

      if (headers.includes("base64")) {
        try {
          const clean = partBody.replace(/[\r\n\s]/g, "");
          decoded = decodeURIComponent(
            atob(clean).split("").map(c =>
              "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
            ).join("")
          );
        } catch {
          decoded = partBody;
        }
      } else if (headers.includes("quoted-printable")) {
        decoded = partBody
          .replace(/=\r?\n/g, "")
          .replace(/=([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
      }

      if (isPlain && !plainText) plainText = decoded.trim();
      if (isHtml && !htmlText) htmlText = decoded.trim();
    }

    return {
      isRawMime: true,
      text: plainText || body,
      html: htmlText,
    };
  }

  // ============================================================
  // Status
  // ============================================================

  get isWorkerActive(): boolean {
    return this.workerAvailable;
  }

  destroy(): void {
    this.teardownWorker();
    this.initPromise = null;
  }
}

// ============================================================
// Singleton Export
// ============================================================

export const emailWorkerService = new EmailWorkerService();
